import * as C from './constants';
import { ILogEventType, IShow, IPlayStatus, ITrack } from './Player.types';

/******************************************************************************
 *                                Log events
 *****************************************************************************/
export const sendLogEvent = (actionData: {
  trackId: string;
  event: ILogEventType;
  trackTime: number;
  trackFinalTime?: number;
}) =>
  ({
    type: C.SEND_LOG_EVENT,
    payload: {
      trackId: actionData.trackId,
      event: actionData.event,
      trackTime: actionData.trackTime,
      ...((actionData.trackFinalTime
        ? { trackFinalTime: actionData.trackFinalTime }
        : undefined) as {
        trackFinalTime?: number;
      }),
    },
  }) as const;

/*******************************************
 *                  Show
 ******************************************/
export const showId = (actionData: { showId: string }) =>
  ({
    type: C.SHOW_ID,
    payload: {
      showId: actionData.showId,
    },
  }) as const;

export const showApiRequestInit = ({
  id,
  limit = 300,
  trackId,
}: {
  id: string;
  limit?: number;
  trackId?: string | null | undefined;
}) =>
  ({
    type: C.SHOW_API_REQUEST_INIT,
    payload: {
      id,
      limit,
      trackId,
    },
  }) as const;

export const showApiRequestSuccess = ({
  data,
  trackId,
}: {
  data: IShow;
  trackId?: string | null | undefined;
}) =>
  ({
    type: C.SHOW_API_REQUEST_SUCCESS,
    payload: {
      data,
      trackId,
    },
  }) as const;

export const showApiRequestFailure = (actionData: { errorMessage: string }) =>
  ({
    type: C.SHOW_API_REQUEST_FAILURE,
    payload: {
      errorMessage: actionData.errorMessage,
    },
  }) as const;

/*******************************************
 *                  Track
 ******************************************/
export const trackApiRequestInit = (actionData: { id: string; setShowIdFromResult?: boolean }) =>
  ({
    type: C.TRACK_API_REQUEST_INIT,
    payload: {
      id: actionData.id,
      setShowIdFromResult: actionData.setShowIdFromResult || false,
    },
  }) as const;

export const trackApiRequestSuccess = (actionData: { track: ITrack }) =>
  ({
    type: C.TRACK_API_REQUEST_SUCCESS,
    payload: {
      track: actionData.track,
    },
  }) as const;

export const trackApiRequestFailure = (actionData: { error: Error }) =>
  ({
    type: C.TRACK_API_REQUEST_FAILURE,
    payload: {
      error: actionData.error,
    },
  }) as const;

/*******************************************
 *                  Player
 ******************************************/
export const togglePlayStatus = () =>
  ({
    type: C.TOGGLE_PLAY_STATUS,
    payload: {},
  }) as const;

export const setPlayStatus = (actionData: { playStatus: IPlayStatus }) =>
  ({
    type: C.SET_PLAY_STATUS,
    payload: {
      playStatus: actionData.playStatus,
    },
  }) as const;

export const setCurrentlyPlayingTrackId = (actionData: { id: null | string }) =>
  ({
    type: C.SET_CURRENTLY_PLAYING_TRACK_ID,
    payload: {
      id: actionData.id,
    },
  }) as const;

export const seeking = (actionData: { isSeeking: boolean }) =>
  ({
    type: C.SEEKING,
    payload: {
      isSeeking: actionData.isSeeking,
    },
  }) as const;

export const trackProgress = (actionData: {
  durationPlayedInPermille: number;
  durationPlayedInSeconds: number | null;
}) => {
  const payload: any = {
    durationPlayedInPermille: actionData.durationPlayedInPermille,
  };

  if (typeof actionData.durationPlayedInSeconds === 'number') {
    payload.durationPlayedInSeconds = actionData.durationPlayedInSeconds;
  }

  return {
    type: C.TRACK_PROGRESS,
    payload,
  };
};

export const trackEnd = (actionData: { trackId: string }) =>
  ({
    type: C.TRACK_END,
    payload: {
      trackId: actionData.trackId,
    },
  }) as const;

export const trackDuration = (actionData: { trackDuration: number }) =>
  ({
    type: C.TRACK_DURATION,
    payload: {
      trackDuration: actionData.trackDuration,
    },
  }) as const;

export type IPlayerAction =
  | ReturnType<typeof sendLogEvent>
  | ReturnType<typeof showId>
  | ReturnType<typeof showApiRequestInit>
  | ReturnType<typeof showApiRequestSuccess>
  | ReturnType<typeof showApiRequestFailure>
  | ReturnType<typeof trackApiRequestInit>
  | ReturnType<typeof trackApiRequestSuccess>
  | ReturnType<typeof trackApiRequestFailure>
  | ReturnType<typeof togglePlayStatus>
  | ReturnType<typeof setPlayStatus>
  | ReturnType<typeof setCurrentlyPlayingTrackId>
  | ReturnType<typeof seeking>
  | ReturnType<typeof trackProgress>
  | ReturnType<typeof trackEnd>
  | ReturnType<typeof trackDuration>;
